import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useFilterContext } from './filtercontext';
import { useRetainedSearchInput, renderSelectionTags } from './multiselectshared';
import styles from './filtermultiselect.module.css';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Helper function to check if a value is "blank"
const isBlankValue = (value) => {
  return value === null ||
         value === undefined ||
         (typeof value === 'string' && value.trim() === '');
};

// Stable module-level default for getOptionLabel — has no per-instance closure.
// MUST NOT be defined inline in the destructure below: every render of
// MultiSelectFilter would create a new function reference, invalidating the
// uniqueOptions useMemo whose deps include it, which makes the registerFilter
// useEffect re-fire every render, which calls setActiveFilters with a new
// filterFn ref, which makes the parent DataFilter's apply-filters useEffect
// re-fire, which cascades into a parent re-render, which renders this
// component again with a new default, and so on. Render loop on stable data.
const defaultGetOptionLabel = value => String(value);

export default function MultiSelectFilter({
  field,
  label,
  getOptionValue: getOptionValueProp,
  getOptionLabel: getOptionLabelProp,
}) {
  const { originalData, registerFilter } = useFilterContext();

  // getOptionValue closes over `field`, so it can't be module-level. Stabilize
  // it with useCallback keyed on `field` so it has identity stability across
  // renders for the same `field` value (same loop hazard as defaultGetOptionLabel).
  const getOptionValue = useCallback(
    getOptionValueProp || (item => item[field]),
    [getOptionValueProp, field],
  );
  const getOptionLabel = getOptionLabelProp || defaultGetOptionLabel;

  const [selectedValues, setSelectedValues] = useState([]);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Memoize the unique options calculation and check for blank values
  const { uniqueOptions, hasBlankValues } = useMemo(() => {
    if (!originalData || !originalData.length) return { uniqueOptions: [], hasBlankValues: false };

    const valueSet = new Set();
    let foundBlank = false;

    originalData.forEach(item => {
      const value = getOptionValue(item);
      if (isBlankValue(value)) {
        foundBlank = true;
      } else {
        valueSet.add(value);
      }
    });

    const options = Array.from(valueSet).sort((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });

    return { uniqueOptions: options, hasBlankValues: foundBlank };
  }, [originalData, getOptionValue, getOptionLabel]);

  // Memoize the clear handler to prevent recreation on every render. Clearing
  // returns the filter to untouched, so later-arriving values are selected too.
  const handleClearFromContext = useCallback(() => {
    const allOptions = hasBlankValues ? ['__no_value__', ...uniqueOptions] : uniqueOptions;
    setSelectedValues([...allOptions]);
    setHasUserInteracted(false);
  }, [uniqueOptions, hasBlankValues]);

  // Select all options until the user touches the filter — including values
  // that arrive in a later load batch (progressive drilldowns), which a
  // first-load-only select-all silently excluded (Trello ZE03rv0c). Untouched,
  // the selection is always the whole option list, so a length mismatch means
  // new options arrived; the length guard stops the effect re-firing.
  const totalOptionCount = uniqueOptions.length + (hasBlankValues ? 1 : 0);
  useEffect(() => {
    if (totalOptionCount > 0 && selectedValues.length !== totalOptionCount && !hasUserInteracted) {
      const allOptions = hasBlankValues ? ['__no_value__', ...uniqueOptions] : uniqueOptions;
      setSelectedValues([...allOptions]);
    }
  }, [uniqueOptions, hasBlankValues, totalOptionCount, selectedValues.length, hasUserInteracted]);

  // Register filter with the context
  useEffect(() => {
    const totalOptions = hasBlankValues ? uniqueOptions.length + 1 : uniqueOptions.length;
    const selectedCount = selectedValues.length;

    let filterFn;
    let filterDescription;

    if (selectedCount === totalOptions && totalOptions > 0) {
      // All items selected = show everything. No description: this filter is
      // registered but narrowing nothing, so it must not appear in a
      // "Filters Applied" list (see describeActiveFilters in filtercontext).
      filterFn = () => true;
      filterDescription = null;
    } else if (selectedCount === 0) {
      // No items selected = show nothing
      filterFn = () => false;
      filterDescription = `${label}: No Values`;
    } else {
      // Some items selected = filter to show only selected items
      const selectedSet = new Set(selectedValues.filter(v => v !== '__no_value__'));
      const includeBlank = selectedValues.includes('__no_value__');

      filterFn = item => {
        const itemValue = getOptionValue(item);
        if (isBlankValue(itemValue)) {
          return includeBlank;
        }
        return selectedSet.has(itemValue);
      };

      if (selectedCount >= 5) {
        filterDescription = `${label}: ${selectedCount} selected`;
      } else {
        const displayValues = selectedValues.map(v =>
          v === '__no_value__' ? '(No Value)' : getOptionLabel(v)
        );
        filterDescription = `${label}: ${displayValues.join(', ')}`;
      }
    }

    const descriptionFn = () => filterDescription;
    registerFilter(field, label, filterFn, handleClearFromContext, descriptionFn);
  }, [
    selectedValues,
    uniqueOptions.length,
    hasBlankValues,
    field,
    label
    // Remove the callback dependencies that cause re-renders
  ]);

  // Handle selection changes
  const handleChange = useCallback((event, newValue) => {
    setHasUserInteracted(true);

    // Check if "Select All" was clicked
    if (newValue.includes('__select_all__')) {
      const totalOptions = hasBlankValues ? uniqueOptions.length + 1 : uniqueOptions.length;
      const allCurrentlySelected = selectedValues.length === totalOptions;

      if (allCurrentlySelected) {
        // Deselect all
        setSelectedValues([]);
      } else {
        // Select all (including __no_value__ if applicable)
        const allOptions = hasBlankValues ? ['__no_value__', ...uniqueOptions] : uniqueOptions;
        setSelectedValues([...allOptions]);
      }
      return;
    }

    // Normal selection (filter out any __select_all__ markers)
    const filteredValues = (newValue || []).filter(value => value !== '__select_all__');
    setSelectedValues(filteredValues);
  }, [selectedValues.length, uniqueOptions, hasBlankValues]);

  // Add "Select All" and optionally "(No Value)" to options
  const optionsWithSpecial = useMemo(() => {
    const options = ['__select_all__'];
    if (hasBlankValues) {
      options.push('__no_value__');
    }
    options.push(...uniqueOptions);
    return options;
  }, [uniqueOptions, hasBlankValues]);

  // Custom option rendering with checkboxes
  const renderOption = useCallback((props, option, { selected }) => {
    const { key, ...optionProps } = props;

    // Handle "Select All" option
    if (option === '__select_all__') {
      const totalOptions = hasBlankValues ? uniqueOptions.length + 1 : uniqueOptions.length;
      const allSelected = selectedValues.length === totalOptions;
      const someSelected = selectedValues.length > 0;

      return (
        <li key={key} {...optionProps}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            indeterminateIcon={<CheckBoxOutlineBlankIcon fontSize="small" style={{ opacity: 0.5 }} />}
            style={{ marginRight: 8 }}
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
          />
          <strong>Select All</strong>
        </li>
      );
    }

    // Handle "(No Value)" option
    if (option === '__no_value__') {
      return (
        <li key={key} {...optionProps}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          <em className={styles.noValue}>(No Value)</em>
        </li>
      );
    }

    // Regular option rendering
    return (
      <li key={key} {...optionProps}>
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={selected}
        />
        {getOptionLabel(option)}
      </li>
    );
  }, [getOptionLabel, selectedValues.length, uniqueOptions.length, hasBlankValues]);

  // Chips: "All (N)", a few individual values, or one "N of M selected"
  const renderTags = useCallback((value, getTagProps) => renderSelectionTags(value, getTagProps, {
    totalCount: hasBlankValues ? uniqueOptions.length + 1 : uniqueOptions.length,
    getLabel: option => (option === '__no_value__' ? '(No Value)' : getOptionLabel(option)),
  }), [uniqueOptions.length, hasBlankValues, getOptionLabel]);

  // Keep the search text while toggling options (MUI clears it on select)
  const { inputValue, onInputChange, onClose } = useRetainedSearchInput();

  // Custom input rendering
  const renderInput = useCallback((params) => (
    <TextField
      {...params}
      label={label}
      size="small"
      placeholder="Search and select..."
    />
  ), [label]);

  // Custom getOptionLabel to handle special options
  const getOptionLabelForAutocomplete = useCallback((option) => {
    if (option === '__select_all__') {
      return 'Select All';
    }
    if (option === '__no_value__') {
      return '(No Value)';
    }
    return getOptionLabel(option);
  }, [getOptionLabel]);

  // Custom filtering to handle special options
  const filterOptions = useCallback((options, { inputValue }) => {
    const filtered = options.filter(option => {
      if (option === '__select_all__' || option === '__no_value__') {
        return true; // Always show special options
      }
      return getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase());
    });
    return filtered;
  }, [getOptionLabel]);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
        {(uniqueOptions.length > 0 || hasBlankValues) && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({selectedValues.length}/{hasBlankValues ? uniqueOptions.length + 1 : uniqueOptions.length})
          </Typography>
        )}
      </Typography>

      <Autocomplete
        multiple
        options={optionsWithSpecial}
        value={selectedValues}
        limitTags={5}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={onInputChange}
        onClose={onClose}
        disableCloseOnSelect
        getOptionLabel={getOptionLabelForAutocomplete}
        filterOptions={filterOptions}
        renderOption={renderOption}
        renderTags={renderTags}
        renderInput={renderInput}
        isOptionEqualToValue={(option, value) => option === value}
        size="small"
        fullWidth
      />
    </Box>
  );
}